import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../redux/features/appData";
import {
  EmployeeRecord,
  EmployeeReferenceType,
  Task,
  TaskStatus,
} from "winslow-core";
import { Button, Icon } from "../../ds2/components";
import { 
  CheckCircle,
  Clock, 
  XCircle,
  AlertCircle,
  Calendar,
  User,
  Workflow,
  Eye,
  Stamp
} from "lucide-react";
import "./Tasks.css";
import { 
  apiApproveTask,
    apiGetTasksForOwner,
    apiGetTasksForOwnerInWorkspace,
    apiRejectTask
} from "../../services/TaskService";
import { apiGetEmployeeRecordsForWorkspace } from "../../services/EmployeeRecordService";
import { erGetCPValue } from "../app/app-employee-records/employeeRecordUtils";
import ModalBasicConfirm from "../common/ModalBasicConfirm";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { apiProcessWorkflowActiveNodes } from "../../services/WorkflowService";
import { AppWorkflowViewer } from "../app/workflow/AppWorkflowViewer";
import { GenericSelect } from "../shared/GenericSelect";
import { openEmployeeProfileSidebar } from "../../redux/features/agent/agentSlice";

export const Tasks = () => {
    const dispatch = useDispatch();
  const storeUser = useSelector(selectUser);
  const uiStates = useSelector(selectUIStates);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | "ALL">("ALL");
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);

  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
  const [showApproveConfirmationModal, setShowApproveConfirmationModal] = useState<boolean>(false);
  const [showRejectConfirmationModal, setShowRejectConfirmationModal] = useState<boolean>(false);
  const [showWorkflowViewer, setShowWorkflowViewer] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);

  useEffect(() => {
    console.log("[Tasks/main] storeUser: ", storeUser);
    dispatch(setUIStates({...uiStates, hrisLocation: "Your Tasks"}));
  }, []);

  useEffect(() => {
    if (storeUser?.employeeRecordId) {
      (async () => { 
        await loadTasks();
        await loadEmployeeRecords();
        setIsLoading(false);
      })();
    } else {
      setIsLoading(false);
    }
  }, [storeUser?.employeeRecordId]);

  const loadEmployeeRecords = async () => {
    try {
      const theEmployeeRecords = await apiGetEmployeeRecordsForWorkspace(storeUser?.currentWorkspaceId || "");
      if (!theEmployeeRecords) {
        setError("Failed to load employee records");
        setEmployeeRecords([]);
        return;
      }
      setEmployeeRecords(theEmployeeRecords);
    }
    catch (err) {
      console.error("Error loading employee records:", err);
      setError("Failed to load employee records");
    }
  };

  const loadTasks = async () => {
    try {
      setError(null);
      
      const theTasks = await apiGetTasksForOwnerInWorkspace(
        storeUser?.employeeRecordId || "",
        storeUser?.currentWorkspaceId || ""
      );

      if (!theTasks) {
        setError("Failed to load tasks");
        setTasks([]);
        return;
      }

      setTasks(theTasks);
    } catch (err) {
      console.error("Error loading tasks:", err);
      setError("Failed to load tasks");
    } finally {
    }
  };

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.WAITING:
      case TaskStatus.ACTIVE:
        return <Icon icon={<Clock size={16} color={getStatusColor(status)} />} size="sm" className="tasks-status-icon tasks-status-unknown" />;
      case TaskStatus.COMPLETE:
        return <Icon icon={<CheckCircle size={16} color={getStatusColor(status)} />} size="sm" className="tasks-status-icon tasks-status-complete" />;
      case TaskStatus.REJECTED:
        return <Icon icon={<XCircle size={16} color={getStatusColor(status)} />} size="sm" className="tasks-status-icon tasks-status-rejected" />;
      default:
        return <Icon icon={<AlertCircle size={16} color={getStatusColor(status)} />} size="sm" className="tasks-status-icon tasks-status-unknown" />;
    }
  };

  const getStatusText = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.WAITING:
      case TaskStatus.ACTIVE:
        return "Needs Review";
      case TaskStatus.COMPLETE:
        return "Complete";
      case TaskStatus.REJECTED:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.WAITING:
      case TaskStatus.ACTIVE:
        return "#f59e0b";
      case TaskStatus.COMPLETE:
        return "#10b981";
      case TaskStatus.REJECTED:
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min${diffInMinutes !== 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    }
    
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  };

  const filteredTasks = selectedStatus === "ALL" 
    ? tasks 
    : tasks.filter(task => task.status === selectedStatus);

  const handleApprove = async() => {

    if (!pendingTaskId) {
      return;
    }

    toast("Approving task...", toastSuccess);
    setShowApproveConfirmationModal(false);

    const updatedTask = await apiApproveTask(pendingTaskId);

    if (!updatedTask) {
      return;
    }

    setTasks(tasks.map(task => task.id === pendingTaskId ? updatedTask : task));
    setPendingTaskId(null);

    toast("Approved.", toastSuccess);

    processWorkflowAfterAction(updatedTask.workflowId || "");
  };

  const handleReject = async() => {
    if (!pendingTaskId) {
      return;
    }
    const updatedTask = await apiRejectTask(pendingTaskId);
    if (!updatedTask) {
      return;
    }
    setTasks(tasks.map(task => task.id === pendingTaskId ? updatedTask : task));
    setPendingTaskId(null);
    setShowRejectConfirmationModal(false);

    toast("Rejected.", toastSuccess);

    processWorkflowAfterAction(updatedTask.workflowId || "");
  };

  const processWorkflowAfterAction = async (workflowId: string) => {

    toast("Processing workflow...", toastSuccess);
    const workflow = await apiProcessWorkflowActiveNodes(workflowId);
    if (!workflow) {
      toast("Failed to process workflow.", toastFail);
      return;
    }

    toast("Done.", toastSuccess);

    console.log("[Tasks/processWorkflowAfterAction] workflow: ", workflow);
  };

  // if () {
  //   return (
  //     <div className="tasks-container">
  //       <div className="tasks-loading">
  //         <div className="tasks-loading-spinner"></div>
  //         <p>Loading tasks...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="tasks-container">
        <div className="tasks-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <Button
            variant="primary"
            size="sm"
            label="Retry"
            onClick={loadTasks}
          />
        </div>
      </div>
    );
  }

  console.log("[Tasks/main] tasks: ", tasks);

  function getAvatarUrl(creatorEmployeeRecordId: string): string | undefined {
    const employeeRecord = employeeRecords.find(er => er.id === creatorEmployeeRecordId);
    return employeeRecord?.avatarURL;
  }

  function getEmployeeName(creatorEmployeeRecordId: string): React.ReactNode {
    const employeeRecord = employeeRecords.find(er => er.id === creatorEmployeeRecordId);

    if (!employeeRecord) {
      return "Unknown Creator";
    }

    return `${erGetCPValue(employeeRecord, "system-firstName")} ${erGetCPValue(employeeRecord, "system-lastName")}`;
  }

  const handleViewEmployeeRecord = (employeeRecordId: string) => {
    dispatch(openEmployeeProfileSidebar({
      employeeId: employeeRecordId,
      type: EmployeeReferenceType.EMPLOYEE_RECORD
    }));
  };

  console.log("[Tasks/main] filteredTasks: ", filteredTasks);

  const statusOptions = [
    { displayName: "All Tasks", value: "ALL" },
    { displayName: "Active", value: TaskStatus.ACTIVE },
    { displayName: "Complete", value: TaskStatus.COMPLETE },
    { displayName: "Rejected", value: TaskStatus.REJECTED },
  ];

  const selectedStatusOption = statusOptions.find(option => option.value === selectedStatus);

  return (
    <div className="tasks-container">
      {/* Header */}
      <div className="tasks-header">
        <div className="tasks-header-left">
          <div className="tasks-filter-group">
            <label>Status:</label>
            <GenericSelect
              selected={selectedStatusOption}
              onChange={(value) => setSelectedStatus(value as TaskStatus | "ALL")}
              options={[
                { displayName: "All Tasks", value: "ALL" },
                { displayName: "Active", value: TaskStatus.ACTIVE },
                { displayName: "Complete", value: TaskStatus.COMPLETE },
                { displayName: "Rejected", value: TaskStatus.REJECTED },
              ]}
            />
            {/* <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as TaskStatus | "ALL")}
              className="tasks-filter-select"
            >
              <option value="ALL">All Tasks</option>
              <option value={TaskStatus.ACTIVE}>Active</option>
              <option value={TaskStatus.COMPLETE}>Complete</option>
              <option value={TaskStatus.REJECTED}>Rejected</option>
            </select> */}
          </div>
        </div>
        <div className="tasks-header-right">
          <Button
            variant="primary"
            size="md"
            label="Refresh"
            onClick={loadTasks}
          />
        </div>
      </div>

      {/* Tasks Table */}
      <div className="tasks-table-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Task</th>
              <th>Process</th>
              <th>Originator</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(filteredTasks.length === 0 && !isLoading) ? (
              <tr>
                <td colSpan={7} className="tasks-empty">
                  <div className="tasks-empty-content">
                    <Calendar size={48} />
                    <h3>No tasks found</h3>
                    <p>
                      {selectedStatus === "ALL" 
                        ? "You don't have any tasks assigned to you."
                        : `You don't have any ${selectedStatus.toLowerCase()} tasks.`
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredTasks.map((task) => (
                <tr key={task.id} className="tasks-row">
                  <td className="tasks-cell">
                    <div className="tasks-type">
                      {task.isApproval ? (
                        <div className="tasks-type-badge tasks-type-approval">
                          <User size={14} />
                          Approval
                        </div>
                      ) : (
                        <div className="tasks-type-badge tasks-type-task">
                          <Workflow size={14} />
                          Task
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="tasks-cell tasks-cell-main">
                    <div className="tasks-task-info">
                      {/* <h3 className="tasks-task-name">{task.name}</h3> */}
                      {task.description && (
                        <p className="tasks-task-description">{task.description}</p>
                      )}
                    </div>
                  </td>

                  <td className="tasks-cell">
                    <div className="tasks-vibe-process">
                      {task.workflow?.vibeProcess ? (
                        <div className="tasks-vibe-process-info">
                          
                          {task?.workflow?.vibeProcess?.name}
                          
                          {/* {task?.workflow?.vibeProcess?.description && (
                            <p className="tasks-vibe-process-description">{task.workflow.vibeProcess.description}</p>
                          )} */}
                        </div>
                      ) : (
                        <span className="tasks-no-vibe-process">No Vibe Process</span>
                      )}
                    </div>
                  </td>

                  <td className="tasks-cell">
                    <div className="tasks-creator">
                      {task.workflow?.creatorEmployeeRecordId ? (
                        <div 
                          className="tasks-creator-info tasks-creator-clickable"
                          onClick={() => handleViewEmployeeRecord(task.workflow?.creatorEmployeeRecordId || "")}
                        >
                          <div className="tasks-creator-avatar">
                              <img src={getAvatarUrl(task.workflow.creatorEmployeeRecordId)} />
                          </div>
                          <div className="tasks-creator-details">
                              {getEmployeeName(task.workflow.creatorEmployeeRecordId)}
                            
                          </div>
                        </div>
                      ) : (
                        <span className="tasks-no-creator">Unknown Creator</span>
                      )}
                    </div>
                  </td>
                  <td className="tasks-cell">
                    <div className="tasks-status">
                      {getStatusIcon(task.status)}
                      <span 
                        className="tasks-status-text"
                        style={{ color: getStatusColor(task.status) }}
                      >
                        {getStatusText(task.status)}
                      </span>
                    </div>
                  </td>
                  <td className="tasks-cell">
                    <div className="tasks-date">
                      {formatTimeAgo(new Date(task.createdAt))}
                    </div>
                  </td>
                  <td className="tasks-cell">
                    <div className="tasks-actions">
                     
                        <Icon 
                          variant="primary"
                          icon={<CheckCircle size={16} color={task.status === TaskStatus.ACTIVE ? "#10b981" : "#9ca3af"} />}
                          onClick={task.status === TaskStatus.ACTIVE ? () => {
                            setPendingTaskId(task.id)
                            setShowApproveConfirmationModal(true)
                          } : undefined}
                          disabled={task.status !== TaskStatus.ACTIVE}
                          size="md"
                        />
                        <Icon 
                          variant="primary"
                          icon={<XCircle size={16} color={task.status === TaskStatus.ACTIVE ? "#ef4444" : "#9ca3af"} />}
                          onClick={task.status === TaskStatus.ACTIVE ? () => {
                            setPendingTaskId(task.id)
                            setShowRejectConfirmationModal(true)
                          } : undefined}
                          disabled={task.status !== TaskStatus.ACTIVE}
                          size="md"
                        />
                        <Icon 
                          variant="primary"
                          icon={<Eye size={16} />}
                          onClick={() => {
                            if (task.workflowId) {
                              setSelectedWorkflowId(task.workflowId);
                              setShowWorkflowViewer(true);
                            }
                          }}
                          size="md"
                        />
                      
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showApproveConfirmationModal && 
        <ModalBasicConfirm
          isOpen={showApproveConfirmationModal}
          title="Approve Task"
          message="Are you sure you want to approve this task?"
          continueLabel="Approve"
          onContinue={handleApprove}
          onClose={() => {
            setShowApproveConfirmationModal(false);
            setPendingTaskId(null);
          }}
        />
      }

      {showRejectConfirmationModal && 
        <ModalBasicConfirm
          isOpen={showRejectConfirmationModal}
          title="Reject Task"
          message="Are you sure you want to reject this task?"
          continueLabel="Reject"
          onContinue={handleReject}
          onClose={() => {
            setShowRejectConfirmationModal(false);
            setPendingTaskId(null);
          }}
        />
      }

      {showWorkflowViewer && selectedWorkflowId && (
        <AppWorkflowViewer
          workflowId={selectedWorkflowId}
          onClose={() => {
            setShowWorkflowViewer(false);
            setSelectedWorkflowId(null);
          }}
        />
      )}
    </div>
  );
};
